import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Monitor,
    Search,
    Filter,
    Download,
    ChevronDown,
    ChevronUp,
    Terminal,
    RefreshCw,
    Wifi,
    WifiOff,
    AlertCircle,
    X,
    Activity,
} from 'lucide-react';
import api from '../api/client';
import useAuthStore, { PERMISSIONS } from '../stores/authStore';
import PCActionModal from '../components/PCActionModal';
import { APPROX_HINT, formatPcHistory, formatRegDate } from '../utils/registrationDate';

// Three-state visual signal, matching CafeDetail.jsx's pcVisualState(): a
// customer actively playing (current_user_id set while online) reads
// distinctly from a PC that's just powered on and idle. The status filter
// and stat counts below key off this too (not raw pc.status) -- "Online"
// and "Idle" used to be separate, OVERLAPPING filter buckets (idle PCs
// are also online), so picking "Online" correctly but confusingly
// returned PCs every one of which was labelled "Idle". In Use/Idle/Offline
// are mutually exclusive, so there's no bucket whose label disagrees with
// what filtering into it shows.
const VISUAL_STATE = {
    inuse: { label: 'In Use', dot: 'online' },
    idle: { label: 'Idle', dot: 'warning' },
    // 'danger' (red), not the global 'offline' (gray) -- see the
    // .status-dot--danger rule below, scoped to this page only.
    offline: { label: 'Offline', dot: 'danger' },
};

const pcVisualState = (pc) => {
    if (pc.status !== 'online') return 'offline';
    return pc.current_user_id ? 'inuse' : 'idle';
};

export default function PCRegistry() {
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = useAuthStore();

    const [pcs, setPCs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // ?q= / ?cafe= pre-filter the list — the header search links here.
    const [searchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
    const [statusFilter, setStatusFilter] = useState('all');
    const [cafeFilter, setCafeFilter] = useState(searchParams.get('cafe') || 'all');

    // Already on this page when a header result is picked: the component
    // doesn't remount, so re-apply the params when they change (React's
    // "adjust state while rendering" pattern, not an effect).
    const paramsKey = searchParams.toString();
    const [appliedParamsKey, setAppliedParamsKey] = useState(paramsKey);
    if (appliedParamsKey !== paramsKey) {
        setAppliedParamsKey(paramsKey);
        setSearchQuery(searchParams.get('q') || '');
        setCafeFilter(searchParams.get('cafe') || 'all');
    }
    const [sortConfig, setSortConfig] = useState({ key: 'cafe_id', direction: 'asc' });
    const [selectedPC, setSelectedPC] = useState(null);
    const [showCommandModal, setShowCommandModal] = useState(false);

    const canRemoteAccess = hasPermission(PERMISSIONS.REMOTE_PC_ACCESS) || isSuperAdmin();

    // Fetch PCs from API — fleet-wide across every café (SuperAdmin view).
    // GET /clientpc/ is per-café and 400s for a SuperAdmin token in prod
    // (no cafe_id on the token), so this page uses the global counterpart.
    const fetchPCs = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.get('/internal/fleet/pcs');
            // Transform API data to match UI expectations
            const transformedPCs = (response.data || []).map(pc => ({
                id: pc.id,
                name: pc.name || `PC-${pc.id}`,
                cafe_id: pc.cafe_id,
                cafe_name: pc.cafe_name || `Café ${pc.cafe_id}`,
                status: pc.status || 'offline',
                os: 'Unknown', // kiosk doesn't report OS today
                client_version: pc.client_version || 'Unknown',
                last_heartbeat: pc.last_seen,
                ip_address: pc.ip_address || 'N/A',
                uptime_hours: 0,
                current_user: pc.user_name || null,
                current_user_id: pc.current_user_id ?? null,
                admin_console_enabled: !!pc.admin_console_enabled,
                // '' rather than null so the column sorts (ISO strings order correctly).
                registered_at: pc.registered_at || '',
                registered_at_approx: !!pc.registered_at_approx,
                uninstalled_at: pc.uninstalled_at || null,
                last_reinstalled_at: pc.last_reinstalled_at || null,
                reinstall_count: pc.reinstall_count || 0,
            }));
            setPCs(transformedPCs);
        } catch (err) {
            console.error('Failed to fetch PCs:', err);
            setError(err.response?.data?.detail || 'Failed to load PCs');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPCs();
        // Refresh every 30 seconds for live status
        const interval = setInterval(fetchPCs, 30000);
        return () => clearInterval(interval);
    }, []);

    // Get unique cafes for filter
    const uniqueCafes = useMemo(() => {
        const cafes = new Map();
        pcs.forEach((pc) => {
            if (!cafes.has(pc.cafe_id)) {
                cafes.set(pc.cafe_id, pc.cafe_name);
            }
        });
        return Array.from(cafes.entries());
    }, [pcs]);

    // Filter and sort PCs
    const filteredPCs = useMemo(() => {
        let result = [...pcs];

        // Search
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(
                (pc) =>
                    pc.name.toLowerCase().includes(query) ||
                    pc.cafe_name.toLowerCase().includes(query) ||
                    pc.ip_address.includes(query)
            );
        }

        // Status filter -- mutually exclusive buckets (In Use / Idle /
        // Offline), same vocabulary as the row labels, via pcVisualState.
        if (statusFilter !== 'all') {
            result = result.filter((pc) => pcVisualState(pc) === statusFilter);
        }

        // Cafe filter
        if (cafeFilter !== 'all') {
            result = result.filter((pc) => pc.cafe_id === parseInt(cafeFilter));
        }

        // Sort
        result.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    }, [pcs, searchQuery, statusFilter, cafeFilter, sortConfig]);

    const handleSort = (key) => {
        setSortConfig((current) => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const formatTimeAgo = (isoString) => {
        const date = new Date(isoString);
        const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

        if (seconds < 60) return `${seconds}s ago`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        return `${Math.floor(seconds / 86400)}d ago`;
    };

    const handleRemoteAccess = (pc) => {
        setSelectedPC(pc);
        setShowCommandModal(true);
    };

    const closeCommandModal = () => {
        setShowCommandModal(false);
        setSelectedPC(null);
    };

    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) return null;
        return sortConfig.direction === 'asc' ? (
            <ChevronUp size={14} />
        ) : (
            <ChevronDown size={14} />
        );
    };

    // Stats, matching the mutually-exclusive In Use / Idle / Offline buckets
    // above -- these used to be online/offline/"warning" (the last one dead,
    // the backend never sets it), which double-counted: an idle PC was also
    // counted under "online", so clicking Online could show PCs every one of
    // which was labelled Idle.
    const stats = useMemo(() => ({
        total: pcs.length,
        inuse: pcs.filter((p) => pcVisualState(p) === 'inuse').length,
        idle: pcs.filter((p) => pcVisualState(p) === 'idle').length,
        offline: pcs.filter((p) => pcVisualState(p) === 'offline').length,
    }), [pcs]);

    return (
        <div className="pc-registry">
            {/* Page Header */}
            <div className="page-header">
                <div className="page-header-content">
                    <div className="page-header-icon">
                        <Monitor size={24} />
                    </div>
                    <div>
                        <h1 className="page-title">PC Registry</h1>
                        <p className="page-subtitle">
                            {stats.inuse} in use, {stats.idle} idle, {stats.offline} offline
                        </p>
                    </div>
                </div>
                <div className="page-header-actions">
                    <button className="btn btn--ghost" onClick={fetchPCs} disabled={isLoading}>
                        <RefreshCw size={16} className={isLoading ? 'spin' : ''} />
                        Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="command-error-banner">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            {/* Quick Stats -- each doubles as a status-filter toggle; pc-stat--active
                marks whichever one matches the current filter, since otherwise
                nothing shows which (if any) is applied. */}
            <div className="pc-stats">
                <div className={`pc-stat pc-stat--total${statusFilter === 'all' ? ' pc-stat--active' : ''}`} onClick={() => setStatusFilter('all')}>
                    <Activity size={20} />
                    <span className="pc-stat-value">{stats.total}</span>
                    <span className="pc-stat-label">Total PCs</span>
                </div>
                <div className={`pc-stat pc-stat--inuse${statusFilter === 'inuse' ? ' pc-stat--active' : ''}`} onClick={() => setStatusFilter('inuse')}>
                    <Wifi size={20} />
                    <span className="pc-stat-value">{stats.inuse}</span>
                    <span className="pc-stat-label">In Use</span>
                </div>
                <div className={`pc-stat pc-stat--idle${statusFilter === 'idle' ? ' pc-stat--active' : ''}`} onClick={() => setStatusFilter('idle')}>
                    <Wifi size={20} />
                    <span className="pc-stat-value">{stats.idle}</span>
                    <span className="pc-stat-label">Idle</span>
                </div>
                <div className={`pc-stat pc-stat--offline${statusFilter === 'offline' ? ' pc-stat--active' : ''}`} onClick={() => setStatusFilter('offline')}>
                    <WifiOff size={20} />
                    <span className="pc-stat-value">{stats.offline}</span>
                    <span className="pc-stat-label">Offline</span>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-bar">
                <div className="search-wrapper">
                    <Search size={18} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by PC name, café, or IP..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-input"
                    />
                    {searchQuery && (
                        <button className="search-clear" onClick={() => setSearchQuery('')}>
                            <X size={16} />
                        </button>
                    )}
                </div>

                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="filter-select"
                >
                    <option value="all">All Statuses</option>
                    <option value="inuse">In Use</option>
                    <option value="idle">Idle</option>
                    <option value="offline">Offline</option>
                </select>

                <select
                    value={cafeFilter}
                    onChange={(e) => setCafeFilter(e.target.value)}
                    className="filter-select"
                >
                    <option value="all">All Cafés</option>
                    {uniqueCafes.map(([id, name]) => (
                        <option key={id} value={id}>
                            {name}
                        </option>
                    ))}
                </select>
            </div>

            {/* PC Grid/Table */}
            <div className="table-wrapper">
                <table className="data-table pc-table">
                    <thead>
                        <tr>
                            <th>Status</th>
                            <th className="th-sortable" onClick={() => handleSort('name')}>
                                PC Name <SortIcon column="name" />
                            </th>
                            <th className="th-sortable" onClick={() => handleSort('cafe_name')}>
                                Café <SortIcon column="cafe_name" />
                            </th>
                            <th>IP Address</th>
                            <th>OS / Client</th>
                            <th className="th-sortable" onClick={() => handleSort('last_heartbeat')}>
                                Last Seen <SortIcon column="last_heartbeat" />
                            </th>
                            <th className="th-sortable" onClick={() => handleSort('registered_at')}>
                                Registered <SortIcon column="registered_at" />
                            </th>
                            <th>Current User</th>
                            <th className="th-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPCs.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="empty-state">
                                    <div className="empty-state-content">
                                        <Monitor size={48} strokeWidth={1} />
                                        <p>No PCs found matching your criteria</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredPCs.map((pc) => {
                                const visual = pcVisualState(pc);
                                const visualConfig = VISUAL_STATE[visual];

                                return (
                                    <tr key={`${pc.cafe_id}-${pc.id}`} className="pc-row">
                                        <td>
                                            <div className={`status-indicator status-indicator--${visual}`}>
                                                <div className={`status-dot status-dot--${visualConfig.dot}`} />
                                                <span>{visualConfig.label}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="pc-name">{pc.name}</span>
                                        </td>
                                        <td>
                                            <a
                                                href={`/cafes/${pc.cafe_id}?pc=${encodeURIComponent(pc.name || '')}`}
                                                className="cafe-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate(`/cafes/${pc.cafe_id}?pc=${encodeURIComponent(pc.name || '')}`);
                                                }}
                                            >
                                                {pc.cafe_name}
                                            </a>
                                        </td>
                                        <td>
                                            <code className="ip-address">{pc.ip_address}</code>
                                        </td>
                                        <td>
                                            <div className="os-info">
                                                <span className="os-name">{pc.os}</span>
                                                <span className="client-version">v{pc.client_version}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`last-seen ${pc.status === 'offline' ? 'last-seen--stale' : ''}`}>
                                                {formatTimeAgo(pc.last_heartbeat)}
                                            </span>
                                        </td>
                                        <td>
                                            <span
                                                className={pc.registered_at && !pc.registered_at_approx ? 'last-seen' : 'last-seen last-seen--stale'}
                                                title={pc.registered_at_approx ? APPROX_HINT : undefined}
                                            >
                                                {formatRegDate(pc.registered_at, pc.registered_at_approx)}
                                            </span>
                                            {formatPcHistory(pc) && (
                                                <div className={`pc-history ${pc.uninstalled_at ? 'pc-history--uninstalled' : ''}`}>
                                                    {formatPcHistory(pc)}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {pc.current_user ? (
                                                <span className="current-user">{pc.current_user}</span>
                                            ) : (
                                                <span className="no-user">—</span>
                                            )}
                                        </td>
                                        <td className="td-center">
                                            <div className="action-buttons">
                                                {canRemoteAccess && (
                                                    <button
                                                        className="action-btn action-btn--primary"
                                                        title={pc.status === 'online' ? 'Remote Access' : 'Remote Access (PC is offline)'}
                                                        disabled={pc.status !== 'online'}
                                                        onClick={() => handleRemoteAccess(pc)}
                                                    >
                                                        <Terminal size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {showCommandModal && selectedPC && (
                <PCActionModal
                    pc={selectedPC}
                    cafeId={selectedPC.cafe_id}
                    onClose={closeCommandModal}
                    onChanged={fetchPCs}
                />
            )}

            <style>{`
        .pc-registry {
          /* noLag rebrand: scoped to this page only. No Figma node exists
             for PC Registry / Fleet — accent-only, not a card redesign
             (see CafeOnboard/Licenses/Billing for the same call). Every
             In Use/Idle/Offline color below is a separate --status-* token,
             including .pc-stat--active's currentColor trick, which
             deliberately rings each stat tile in ITS OWN semantic color —
             none of that is touched by this scoping. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);

          max-width: 1600px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--space-8);
        }

        .page-header-content {
          display: flex;
          align-items: center;
          gap: var(--space-4);
        }

        .page-header-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-lg);
          /* Was a two-stop gradient hardcoding --accent-secondary directly
             (purple) alongside --accent-primary — this page's rebrand
             scoping only overrides the latter, so that gradient would have
             stayed half-purple/half-orange. var(--accent-gradient) is the
             single token every other page's header icon already uses
             (e.g. SystemHealth.jsx) and becomes a clean orange→red once
             scoped above. */
          background: var(--accent-gradient);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .page-title {
          font-size: var(--text-2xl);
          font-weight: 700;
          margin-bottom: var(--space-1);
        }

        .page-subtitle {
          font-size: var(--text-sm);
          color: var(--text-tertiary);
        }

        .command-error-banner {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-4);
          margin-bottom: var(--space-6);
          background: var(--status-danger-subtle);
          color: var(--status-danger);
          border-radius: var(--radius-md);
          font-size: var(--text-sm);
        }

        /* PC Stats */
        .pc-stats {
          display: flex;
          gap: var(--space-4);
          margin-bottom: var(--space-6);
        }

        .pc-stat {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-4) var(--space-6);
          background: var(--glass-bg);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all var(--duration-fast) var(--ease-out);
        }

        .pc-stat:hover {
          border-color: var(--border-hover);
          transform: translateY(-2px);
        }

        .pc-stat--total { color: var(--text-secondary); }
        .pc-stat--inuse { color: var(--status-success); }
        .pc-stat--offline { color: var(--status-danger); }
        .pc-stat--idle { color: var(--status-warning); }

        .pc-stat--active {
          border-color: currentColor;
          box-shadow: 0 0 0 1px currentColor;
          background: var(--glass-bg-elevated);
        }

        .pc-stat-value {
          font-size: var(--text-xl);
          font-weight: 700;
          font-family: var(--font-mono);
        }

        .pc-stat-label {
          font-size: var(--text-sm);
          color: var(--text-tertiary);
        }

        /* Filters */
        .filters-bar {
          display: flex;
          gap: var(--space-4);
          margin-bottom: var(--space-6);
          flex-wrap: wrap;
        }

        .search-wrapper {
          position: relative;
          flex: 1;
          min-width: 280px;
          max-width: 400px;
        }

        .search-icon {
          position: absolute;
          left: var(--space-4);
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-tertiary);
        }

        .search-input {
          width: 100%;
          padding: var(--space-2) var(--space-4);
          padding-left: calc(var(--space-4) + 18px + var(--space-2));
          font-family: var(--font-sans);
          font-size: var(--text-sm);
          color: var(--text-primary);
          background: var(--bg-subtle);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-md);
        }

        .search-input:focus {
          outline: none;
          border-color: var(--accent-primary);
        }

        .search-clear {
          position: absolute;
          right: var(--space-2);
          top: 50%;
          transform: translateY(-50%);
          padding: var(--space-1);
          background: transparent;
          border: none;
          color: var(--text-tertiary);
          cursor: pointer;
        }

        .filter-select {
          padding: var(--space-2) var(--space-6) var(--space-2) var(--space-4);
          font-family: var(--font-sans);
          font-size: var(--text-sm);
          color: var(--text-primary);
          background: var(--bg-subtle);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-md);
          cursor: pointer;
        }

        /* Table */
        .table-wrapper {
          background: var(--glass-bg);
          backdrop-filter: blur(20px);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-lg);
          /* Was overflow: hidden with no cap — a long fleet list just kept
             growing the page. auto + max-height scrolls the table inside
             its own box instead, keeping the page's height fixed. */
          overflow: auto;
          max-height: 420px;
        }

        .pc-table {
          min-width: 1000px;
        }

        .th-sortable {
          cursor: pointer;
        }

        .th-center {
          text-align: center;
        }

        .td-center {
          text-align: center;
        }

        .status-indicator {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          font-size: var(--text-sm);
        }

        .status-indicator--inuse { color: var(--status-success); }
        .status-indicator--offline { color: var(--status-danger); }
        .status-indicator--idle { color: var(--status-warning); }

        .status-dot--danger {
          background: var(--status-danger);
          box-shadow: 0 0 8px var(--status-danger);
        }

        .pc-name {
          font-weight: 500;
          font-family: var(--font-mono);
        }

        .cafe-link {
          color: var(--accent-primary);
        }

        .cafe-link:hover {
          text-decoration: underline;
        }

        .ip-address {
          font-family: var(--font-mono);
          font-size: var(--text-sm);
          padding: 2px 6px;
          background: var(--bg-subtle);
          border-radius: var(--radius-sm);
        }

        .os-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .os-name {
          font-size: var(--text-sm);
        }

        .client-version {
          font-size: var(--text-xs);
          color: var(--text-tertiary);
          font-family: var(--font-mono);
        }

        .last-seen {
          font-size: var(--text-sm);
        }

        /* Uninstall / reinstall history under the Registered date. */
        .pc-history { font-size: var(--text-xs); color: var(--text-tertiary); margin-top: 2px; white-space: nowrap; }
        .pc-history--uninstalled { color: var(--status-danger); }
        .last-seen--stale {
          color: var(--text-tertiary);
        }

        .current-user {
          font-size: var(--text-sm);
          color: var(--accent-primary);
        }

        .no-user {
          color: var(--text-tertiary);
        }

        .action-buttons {
          display: flex;
          gap: var(--space-1);
          justify-content: center;
        }

        .action-btn {
          padding: var(--space-1);
          background: transparent;
          border: 1px solid var(--border-default);
          border-radius: var(--radius-sm);
          color: var(--text-secondary);
          cursor: pointer;
          transition: all var(--duration-fast) var(--ease-out);
        }

        .action-btn:hover:not(:disabled) {
          color: var(--accent-primary);
          border-color: var(--accent-primary);
        }

        .action-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .action-btn--primary {
          /* Was a hardcoded cyan (rgba(0,240,255,.1)) that never tracked
             --accent-primary at all — orphaned from the border/text color
             right next to it. var(--accent-primary-subtle) is the same
             "subtle accent fill + accent border/text" pattern used
             everywhere else (badges, stat-card icons, etc). */
          background: var(--accent-primary-subtle);
          border-color: var(--accent-primary);
          color: var(--accent-primary);
        }

        .action-btn--primary:disabled {
          background: transparent;
          border-color: var(--border-default);
          color: var(--text-secondary);
        }

        .empty-state {
          padding: var(--space-16) !important;
        }

        .empty-state-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--space-4);
          color: var(--text-tertiary);
        }

      `}</style>
        </div>
    );
}
